import React, { FC, Ref, useContext, useRef } from 'react'
import { Popup } from '../catalog/Popup'
import styled from 'styled-components'
import ValidationButton from '../catalog/ValidationButton'
import { Web3Context } from '../../hooks/web3'
import { accountShortcut } from '../../utils/account'
import { EmptyLine } from '../catalog/EmptyLine'
import { useTransactions } from '../transactions/TransactionContext'
import { ChainConfiguration } from '../../utils/types'
import { Transaction } from '../../logic/transactions/transactions'
import { SimpleLoader } from '../catalog/SwaapLoader'
import { SmallCheckCircle } from '../catalog/icons/CheckCircle'
import { SmallRedCross } from '../catalog/icons/Cross'

interface Props {
  onExit: VoidFunction
  onDisconnect: VoidFunction
}

const TransactionsSection = styled.div`
  color: ${({ theme }) => theme.walletPendingTxsTextColor};
  border-top: 1px solid ${({ theme }) => theme.walletPendingTxsBackgroundColor};
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: center;
  width: 100%;
`

const Transactions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-height: 300px;
  overflow: auto;
`

export const DisconnectPopup: FC<Props> = (props: Props) => {
  const { onExit, onDisconnect } = props
  const { web3, account, config } = useContext(Web3Context)
  const { transactions, dispatcher } = useTransactions()
  // TODO: make the sorting actually works
  const recentTransactions: Transaction[] = transactions.all
  const hasRecentTransactions = recentTransactions.length > 0

  if (!web3 || !account) {
    console.error('The account should be connected')
    return <div />
  }

  const ref = useRef()
  return (
    <PopupWrapper>
      <Popup outsideRef={ref} onExit={onExit} title={'Account'}>
        <div style={{ width: '300px' }}>
          <EmptyLine />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="light">Connected with:</div>
            <WalletAddress className="mt10">
              {accountShortcut(account)}
            </WalletAddress>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{ width: '75%' }}>
              <ValidationButton isReady onClick={onDisconnect}>
                {'Disconnect'}
              </ValidationButton>
            </span>
          </div>
          {hasRecentTransactions ? (
            <TransactionsSection className="mt20">
              <span className="light mb5">Recent Transactions:</span>
              <Transactions>
                {recentTransactions.map((tx, idx) => {
                  return getTxLink(config, tx)
                })}
              </Transactions>
            </TransactionsSection>
          ) : (
            <></>
          )}
          <EmptyLine />
        </div>
      </Popup>
    </PopupWrapper>
  )
}

export function getTxLink(config: ChainConfiguration, tx: Transaction) {
  let message = 'tx'
  if (tx.type === 'swap' && tx.swap) {
    message = `Swap: ${tx.swap.tokenIn.symbol} to ${tx.swap.tokenOut.symbol}`
  } else if (
    tx.type === 'deposit-multi' &&
    tx.deposit &&
    tx.deposit.amounts &&
    tx.deposit.amounts.keys
  ) {
    message = `Deposit: ${Array.from(tx.deposit.amounts.keys())
      .map(t => t.symbol)
      .join('/')}`
  } else if (tx.type === 'deposit-one' && tx.data && tx.data.token) {
    message = `Deposit: ${tx.data.token.symbol}`
  } else if (
    tx.type === 'withdraw-multi' &&
    tx.withdrawal &&
    tx.withdrawal.amounts
  ) {
    message = `Withdrawal: ${Array.from(tx.withdrawal.amounts.keys())
      .map(t => t.symbol)
      .join('/')}`
  } else {
    console.info(
      'Not found case for Tx message , may need migration: ',
      tx.type
    )
  }
  message += '  ↗'
  return (
    <div
      style={{
        display: 'flex',
        width: '90%',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      key={tx.nonce}
    >
      <a
        className="mt5 link fs12"
        href={config.getExplorerUrl(tx.submitted.hash)}
        target="_blank"
      >
        {message}
      </a>
      {getTxStatusIcon(tx.status)}
    </div>
  )
}

function getTxStatusIcon(status: string) {
  switch (status) {
    case 'pending':
      return <SimpleLoader />
    case 'success':
      return <SmallCheckCircle />
    case 'failed':
      return <SmallRedCross />
    default:
      return <></>
  }
}

function getLink(explorer: string, hash: string): string {
  return `${explorer}/tx/${hash}`
}

// TODO: include this in the Popup component
const PopupWrapper = styled.div`
  font-size: 1.6rem;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  z-index: 100;
`
const WalletAddress = styled.button`
  font-family: Montserrat-Medium;
  color: ${({ theme }) => theme.walletTextColor};
  background-color: ${({ theme }) => theme.walletBackgroundColor};
  box-shadow: ${({ theme }) => theme.walletShadow};
  border-radius: 2.4rem;
  padding: 0.7rem 1.4rem;
  font-size: 2.25rem;
  text-align: center;
  align-self: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.walletBorderColor};

  /* Mobiles */
  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`
